/**
 * Chooses the start time for an in-flight judge display. A server-selected
 * start remains fixed, while a client fallback may be upgraded once the
 * authoritative server stamp arrives.
 */
export type JudgeDisplayStart = {
  start: number;
  source: "server" | "client";
};

export function selectJudgeDisplayStart(
  serverStartedAt: string | null | undefined,
  clientStartedAt: number,
  now: number,
  existingDisplayStart?: JudgeDisplayStart | null
): JudgeDisplayStart {
  const safeNow = Number.isFinite(now) ? now : 0;
  const fallback = Number.isFinite(clientStartedAt) ? clientStartedAt : safeNow;
  const serverStart = serverStartedAt ? Date.parse(serverStartedAt) : Number.NaN;
  const hasValidServerStart = Number.isFinite(serverStart) && serverStart <= safeNow;

  const existing = existingDisplayStart;
  if (existing?.source === "server" && Number.isFinite(existing.start)) return existing;
  if (existing?.source === "client" && Number.isFinite(existing.start)) {
    return hasValidServerStart
      ? { start: serverStart, source: "server" }
      : existing;
  }

  return hasValidServerStart
    ? { start: serverStart, source: "server" }
    : { start: fallback, source: "client" };
}

/** Returns a non-negative whole-second elapsed duration for the judge UI. */
export function judgeElapsedSeconds(start: number, now: number): number {
  if (!Number.isFinite(start) || !Number.isFinite(now)) return 0;
  return Math.max(0, Math.floor((now - start) / 1000));
}
